import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  Add,
} from '@mui/icons-material';
import { serialNumberService } from '../services/serialNumberService';
import { activityService } from '../services/activityService';
import type { SerialNumber } from '../services/serialNumberService';
import type { Activity } from '../types/activity';
import { ScanIconButton, useConfirm } from './common/components';
import type { ScanResult } from './common/components';
import { formatDateTime } from '../utils/dateTimeUtils';

export default function SerialNumberPage() {
  const { confirm, confirmDialog } = useConfirm();
  const { activityId } = useParams<{ activityId: string }>();
  const navigate = useNavigate();
  
  const [activity, setActivity] = useState<Activity | null>(null);
  const [serialNumbers, setSerialNumbers] = useState<SerialNumber[]>([]);
  const [newSerialNumber, setNewSerialNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  useEffect(() => {
    if (!activityId) return;
    let cancelled = false;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load activity data
        const activityData = await activityService.getActivityById(parseInt(activityId));
        if (cancelled) return;
        setActivity(activityData);

        // Load serial numbers
        const serialNumbersData = await serialNumberService.getByActivityId(parseInt(activityId));
        if (cancelled) return;
        setSerialNumbers(serialNumbersData);
      } catch (err) {
        console.error('Error loading data:', err);
        if (!cancelled) setError('Не вдалося завантажити дані');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [activityId]);

  const isDuplicateSerialNumber = (value: string) => {
    const trimmed = value.trim().toLowerCase();
    return serialNumbers.some(sn => sn.serialNumber.toLowerCase() === trimmed);
  };

  const handleSerialNumberChange = (value: string) => {
    setNewSerialNumber(value);
    const trimmed = value.trim();
    setDuplicateError(
      trimmed !== '' && isDuplicateSerialNumber(trimmed)
        ? `Серійний номер "${trimmed}" вже існує в цій активності`
        : null
    );
  };

  const handleScanned = async (value: string): Promise<ScanResult> => {
    const trimmed = value.trim();

    if (!activityId || !trimmed) {
      return { ok: false, message: 'Порожній код' };
    }

    if (isDuplicateSerialNumber(trimmed)) {
      return { ok: false, message: `Вже існує: ${trimmed}` };
    }

    try {
      const newSerial = await serialNumberService.create({
        activityId: parseInt(activityId),
        serialNumber: trimmed,
      });

      setSerialNumbers(prev => [newSerial, ...prev]);
      return { ok: true, message: `Додано: ${trimmed}` };
    } catch (err) {
      console.error('Error adding scanned serial number:', err);
      return { ok: false, message: `Не вдалося додати: ${trimmed}` };
    }
  };

  const handleAddSerialNumber = async () => {
    const trimmed = newSerialNumber.trim();
    if (!activityId || !trimmed) return;

    if (isDuplicateSerialNumber(trimmed)) {
      setDuplicateError(`Серійний номер "${trimmed}" вже існує в цій активності`);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const newSerial = await serialNumberService.create({
        activityId: parseInt(activityId),
        serialNumber: trimmed,
      });

      setSerialNumbers(prev => [newSerial, ...prev]);
      setNewSerialNumber('');
      setDuplicateError(null);
    } catch (err: unknown) {
      console.error('Error adding serial number:', err);
      const axiosError = err as { response?: { data?: string } };
      setError(axiosError?.response?.data || 'Не вдалося додати серійний номер');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSerialNumber = async (id: number) => {
    if (!(await confirm('Ви впевнені, що хочете видалити цей серійний номер?'))) {
      return;
    }

    try {
      setError(null);
      await serialNumberService.delete(id);
      setSerialNumbers(prev => prev.filter(sn => sn.id !== id));
    } catch (err) {
      console.error('Error deleting serial number:', err);
      setError('Не вдалося видалити серійний номер');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !saving) {
      handleAddSerialNumber();
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Активність не знайдена</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Назад
        </Button>
      </Box>
    );
  }

  const serialNumberCount = serialNumbers.length;
  const productAmount = activity.amount;
  const isComplete = serialNumberCount === productAmount;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          size="small"
        >
          Назад
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Серійні номери
        </Typography>
      </Box>

      {/* Activity Info Card */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {activity.productName}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Активність: {activity.activityTypeName}
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Summary */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label={`Кількість продукту: ${productAmount}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`Додано серійних номерів: ${serialNumberCount}`}
            color={isComplete ? 'success' : 'warning'}
            variant="outlined"
          />
        </Box>

        {!isComplete && (
          <Alert severity={serialNumberCount < productAmount ? 'info' : 'warning'} sx={{ mt: 2 }}>
            {serialNumberCount < productAmount
              ? `Потрібно додати ще ${productAmount - serialNumberCount} серійних номерів`
              : `Додано ${serialNumberCount - productAmount} зайвих серійних номерів`
            }
          </Alert>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Add Serial Number */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Додати новий серійний номер
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 2,
          }}
        >
          <TextField
            label="Серійний номер"
            value={newSerialNumber}
            onChange={(e) => handleSerialNumberChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            fullWidth
            placeholder="Введіть серійний номер"
            error={!!duplicateError}
            helperText={duplicateError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <ScanIconButton
                    onDetected={handleScanned}
                    mode="continuous"
                    disabled={saving}
                    tooltip="Сканувати серійні номери"
                  />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddSerialNumber}
            disabled={!newSerialNumber.trim() || !!duplicateError || saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Add />}
            fullWidth
          >
            Додати
          </Button>
        </Box>
      </Paper>

      {/* Serial Numbers List */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" gutterBottom>
          Список серійних номерів ({serialNumberCount})
        </Typography>

        {serialNumbers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Ще немає серійних номерів
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Додайте перший серійний номер використовуючи форму вище
            </Typography>
          </Box>
        ) : (
          <List>
            {serialNumbers.map((serial, index) => (
              <ListItem
                key={serial.id}
                divider={index < serialNumbers.length - 1}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteSerialNumber(serial.id)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Typography variant="h6" component="span">
                      {serial.serialNumber}
                    </Typography>
                  }
                  secondary={`Створено: ${formatDateTime(serial.createdDate)}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {confirmDialog}
    </Box>
  );
}
